/**
 * <out-of-stock-label>
 *
 * Shows/hides the "Currently Out of Stock" text next to a swatch option's
 * selected value, kept in sync with the currently selected colour.
 *
 * Rather than listen to the `product:variant-change` event (which is dispatched
 * inconsistently for yarn products — the dye-lot picker deliberately suppresses
 * it for same-colour changes, and the colour→lot resolution fires in two steps),
 * this reads the authoritative DOM state the theme's VariantAvailability already
 * maintains: the *selected* swatch button carries the `disabled` class exactly
 * when no variant of that colour is available. A MutationObserver keeps the label
 * in lock-step with that class regardless of which code path caused the change.
 *
 * Markup contract:
 *   - `data-option-name="optionN"` — the option this label belongs to.
 *   - Lives inside a `.product__option` that also contains the matching swatch
 *     group `[data-product-option="optionN"]`.
 */
(function () {
  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('out-of-stock-label')) return;

  var OutOfStockLabel = (function () {
    function OutOfStockLabel() {
      return Reflect.construct(HTMLElement, [], OutOfStockLabel);
    }

    OutOfStockLabel.prototype = Object.create(HTMLElement.prototype);
    OutOfStockLabel.prototype.constructor = OutOfStockLabel;

    OutOfStockLabel.prototype.connectedCallback = function () {
      var option = this.getAttribute('data-option-name');
      var container = this.closest('.product__option');
      if (!container) return;

      // Prefer the explicitly-matched swatch group; fall back to any swatches
      // within this option block.
      this.swatchGroup =
        (option && container.querySelector('[data-product-option="' + option + '"]')) ||
        container.querySelector('.product__color-swatches') ||
        container;

      this._update = this.update.bind(this);
      this.observer = new MutationObserver(this._update);
      this.observer.observe(this.swatchGroup, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });

      // Sync once now (deferred so the theme's initial availability pass has run).
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(this._update);
      } else {
        this.update();
      }
    };

    OutOfStockLabel.prototype.disconnectedCallback = function () {
      if (this.observer) this.observer.disconnect();
    };

    OutOfStockLabel.prototype.update = function () {
      var selected = this.swatchGroup.querySelector('.product__color-swatch.selected');
      if (!selected) return;
      this.hidden = !selected.classList.contains('disabled');
    };

    return OutOfStockLabel;
  })();

  customElements.define('out-of-stock-label', OutOfStockLabel);
})();
